import React, { useEffect, useState } from "react";
import Input from "../../form/Input";
import TimePicker from "../../form/timePicker/TimePicker";
import DatePicker from "../../form/DatePicker";
import Accordian from "../Accordian";
import AdvancedSettingsTicket from "../accordianContent/AdvancedSettingsTicket";
import Form from "../../form/Form";
import useForm from "../../../hooks/useForm";
import Button from "../../form/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ticketAddEdit } from "../../../services/ticketService";
import { useOrganization } from "../../../context/OrganizationContext";
import { addTimeToDate, parseTimeString } from "../../../helpers";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Checkbox from "../../form/Checkbox";
import { classNames } from "../../../helpers/classNames";
import { t } from "i18next";
import LangSwitch from "../LangSwitch";

const validation = {
  name: { required: true, message: "Please enter ticket name" },
  quantity: { required: true, message: "quantity is required" },
  salesStartDate: { required: true, message: "start date is required" },
  salesStartTime: { required: true, message: "start time is required" },
  salesEndDate: { required: true, message: "end date is required" },
  salesEndTime: { required: true, message: "end time is required" },
  price: { type: "number", message: "invalid input number (0-9)" },
  fullPrice: { type: "number", message: "invalid input number (0-9)" },
};

const PaidTicket = ({
  eventDetails = {},
  paymentType = {},
  ticketDetailsData = {},
  getResponse = () => {},
  selectedLang,
  currencySymbol = "$",
}) => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const queryClient = useQueryClient();
  const { organizations, selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
    resetField,
  } = useForm({
    validationRules: validation,
    initialState: { fullPaid: "yes" },
  });

  useEffect(() => {
    return () => {
      resetField({});
    };
  }, []);

  const faqData = [
    {
      title: t("Advanced settings"),
      content: (
        <AdvancedSettingsTicket
          values={values}
          handleFormChange={handleChange}
        />
      ),
    },
  ];

  const {
    mutate: addPaidTicket,
    isPending,
    error,
  } = useMutation({
    mutationFn: async (value) => {
      const payload = {
        ...value,
        name: { ...ticketDetailsData?.name, [selectedLang]: value?.name },
        paymentType,
        salesStartDate: value?.salesStartDate
          ? addTimeToDate(
              value?.salesStartDate,
              parseTimeString(value?.salesStartTime)
            )
          : null,
        salesEndDate: value?.salesEndDate
          ? addTimeToDate(
              value?.salesEndDate,
              parseTimeString(value?.salesEndTime)
            )
          : null,
        excerpt: {
          ...ticketDetailsData?.excerpt,
          [selectedLang]: value?.excerpt,
        },
        pickupAtEvent: values?.pickupAtEvent ? "yes" : "no",
        eTicket: values?.eTicket ? "yes" : "no",
        showTicketOnSale: values?.showTicketOnSale ? "yes" : "no",
        event: eventDetails?._id,
        organization: selectedOrganization,
        ...(ticketDetailsData?._id && { id: ticketDetailsData?._id }),
        ...(values?.fullPaid === "yes" && { fullPrice: value?.price }),
      };
      return await ticketAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        if (res?.status === 201) {
          queryClient.removeQueries({ queryKey: ["ticket-details", ticketId] });
          navigate(`/${lang}/user/events/tickets/${eventDetails?.uid}`);
          return;
        } else {
          queryClient.setQueryData(["ticket-details", ticketId], (state) => {
            const state_data = JSON.parse(JSON.stringify(state || {}));
            state_data.data = res?.data;
            return { ...state_data };
          });
        }
      } else if (res?.status === 500) {
        toast.error(res?.error || res?.message || "Server Error");
      } else {
        toast.error(res?.error || res?.message);
      }
    },
  });

  useEffect(() => {
    if (ticketDetailsData?._id) {
      setFieldsValue({
        name: ticketDetailsData?.name?.[selectedLang],
        quantity: ticketDetailsData?.quantity,
        ...(paymentType === "donation" && { quantityType: "unlimited" }),
        price: paymentType !== "free" ? ticketDetailsData?.price : 0,
        fullPaid: ticketDetailsData?.fullPaid,
        fullPrice: ticketDetailsData?.fullPrice,
        salesStartDate: ticketDetailsData?.salesStartDate
          ? ticketDetailsData?.salesStartDate
          : "",
        salesEndDate: ticketDetailsData?.salesEndDate
          ? ticketDetailsData?.salesEndDate
          : "",
        salesStartTime: ticketDetailsData?.salesStartDate
          ? dayjs(ticketDetailsData?.salesStartDate).format("hh:mm:ss A")
          : "",
        salesEndTime: ticketDetailsData?.salesEndDate
          ? dayjs(ticketDetailsData?.salesEndDate).format("hh:mm:ss A")
          : "",
        excerpt: ticketDetailsData?.excerpt?.[selectedLang] || "",
        visibility: ticketDetailsData?.visibility,
        quantityMin: ticketDetailsData?.quantityMin,
        quantityMax: ticketDetailsData?.quantityMax,
        salesChannel: ticketDetailsData?.salesChannel,
        showTicketOnSale: ticketDetailsData?.showTicketOnSale === "yes",
        eTicket: ticketDetailsData?.eTicket === "yes",
        pickupAtEvent: ticketDetailsData?.pickupAtEvent === "yes",
        bookedPaymentEndDate: ticketDetailsData?.bookedPaymentEndDate,
      });
    }
  }, [ticketDetailsData, selectedLang]);

  return (
    <Form
      className="space-y-4"
      handleSubmit={handleSubmit}
      onSubmit={addPaidTicket}
    >
      <Input
        labelClasses={"!text-lg !text-deep-purple !mb-2"}
        inputClasses={
          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
        }
        label={t("Ticket name")}
        onChange={handleChange}
        value={values?.name}
        inputName={"name"}
        {...formValidate(errors, "name")}
      />
      <div className="grid grid-cols-12 gap-4">
        <div
          className={classNames(
            "space-y-4",
            paymentType !== "free" ? "col-span-12 md:col-span-6" : "col-span-12"
          )}
        >
          <div className="relative">
            <Input
              label={t("Available quantity")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              inputClasses={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              inputName={"quantity"}
              inputType="number"
              onKeyDown={(e) => {
                const blockedKeys = ["+", "-", "e", "E", " ", "."];
                if (
                  blockedKeys.includes(e.key) ||
                  (e.key === "=" && e.shiftKey)
                ) {
                  e.preventDefault();
                  return;
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  e.target.value = value;
                  handleChange(e);
                }
              }}
              value={values?.quantity}
              {...formValidate(errors, "quantity")}
            />
          </div>
          {paymentType !== "free" && (
            <div className="relative">
              <Checkbox
                checkboxLabel={t("Enable initial deposite")}
                checkboxName={"fullPaid"}
                checked={values?.fullPaid === "no"}
                onChange={(e) => {
                  const set_value = { ...values, price: 0 };
                  delete set_value?.fullPrice;
                  setFieldsValue({
                    ...set_value,
                    [e.target.name]: e.target.checked ? "no" : "yes",
                  });
                }}
              />
            </div>
          )}
        </div>
        {paymentType !== "free" && (
          <div className="col-span-12 md:col-span-6 space-y-4">
            <div className="relative w-full">
              <Input
                label={t("Price")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                inputClasses={
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !pl-10"
                }
                inputPrefix={currencySymbol}
                inputName={values?.fullPaid === "yes" ? "price" : "fullPrice"}
                onChange={handleChange}
                note={"Please enter a valid price greater than 50¢ (USD)."}
                value={
                  values?.price || values?.fullPrice
                    ? values?.fullPaid === "yes"
                      ? values?.price
                      : values?.fullPrice
                    : 0
                }
              />
            </div>
            <div
              className={classNames(
                "relative w-full flex flex-col md:flex-row gap-4",
                values?.fullPaid === "yes" ? "opacity-40" : "opacity-100"
              )}
            >
              <Input
                label={t("Initial deposite")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                inputClasses={classNames(
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !pl-10"
                )}
                isDisabled={values?.fullPaid === "yes"}
                inputPrefix={currencySymbol}
                note={"Please enter a valid price greater than 50¢ (USD)."}
                inputName={"price"}
                {...(values?.fullPaid === "no"
                  ? {
                      onChange: handleChange,
                      value: values?.price || 0,
                    }
                  : { value: 0 })}
              />
              <DatePicker
                label={t("Booking deadline")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                placeholder={"Select Date"}
                inputClassName={
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                }
                onChange={(val) => {
                  handleChange({
                    target: {
                      name: "bookedPaymentEndDate",
                      value: val?.startDate,
                    },
                  });
                }}
                value={{
                  startDate: values?.bookedPaymentEndDate,
                  endDate: values?.bookedPaymentEndDate,
                }}
                {...formValidate(errors, "bookedPaymentEndDate")}
              />
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="relative">
          <DatePicker
            label={t("Sales start")}
            labelClasses={"!text-lg !text-deep-purple !mb-2"}
            placeholder={"Select Date"}
            inputClassName={
              "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
            }
            onChange={(val) => {
              handleChange({
                target: { name: "salesStartDate", value: val?.startDate },
              });
            }}
            value={{
              startDate: values?.salesStartDate,
              endDate: values?.salesStartDate,
            }}
            {...formValidate(errors, "salesStartDate")}
          />
        </div>
        <div className="relative">
          <TimePicker
            label={t("Start Time")}
            labelClasses={"!text-lg !text-deep-purple !mb-2"}
            inputClassName={
              "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
            }
            onChange={(val) => {
              handleChange({
                target: { name: "salesStartTime", value: val },
              });
            }}
            value={values?.salesStartTime}
            {...formValidate(errors, "salesStartTime")}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="relative">
          <DatePicker
            label={t("Sales end")}
            labelClasses={"!text-lg !text-deep-purple !mb-2"}
            placeholder={"Select Date"}
            inputClassName={
              "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
            }
            // minDate={values?.salesStartDate}
            onChange={(val) => {
              handleChange({
                target: { name: "salesEndDate", value: val?.startDate },
              });
            }}
            value={{
              startDate: values?.salesEndDate,
              endDate: values?.salesEndDate,
            }}
            {...formValidate(errors, "salesEndDate")}
          />
        </div>
        <div className="relative">
          <TimePicker
            label={t("End Time")}
            labelClasses={"!text-lg !text-deep-purple !mb-2"}
            inputClassName={
              "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
            }
            onChange={(val) => {
              handleChange({
                target: { name: "salesEndTime", value: val },
              });
            }}
            value={values?.salesEndTime}
            {...formValidate(errors, "salesEndTime")}
          />
        </div>
      </div>
      <div className="relative">
        <Accordian
          data={faqData}
          className="bg-gray-50 "
          buttonClass="text-deep-purple text-lg h-12"
          contentClass="text-gray-700"
        />
      </div>
      <div className="flex mt-7 gap-3 justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2] pr-6">
        <Button
          variant="secondary"
          buttonLabel={t("Cancel")}
          buttonLabelClasses={"text-lg"}
          buttonClasses={"px-8"}
          buttonFunction={() => {
            navigate(`/${lang}/user/events/tickets/${eventDetails?.uid}`);
          }}
        />
        <Button
          buttonType="submit"
          buttonLabel={t(ticketDetailsData?._id ? "Update" : "Save")}
          buttonLabelClasses={"text-lg"}
          buttonClasses={"px-8"}
          {...(isPending && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </Form>
  );
};

export default PaidTicket;
